import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import type { Page } from '../../types/Page';
import { t } from '../../i18n/t';
import { resolveIcon } from './sidebarIcons';

interface SidebarSearchProps {
  pages: Page[];
}

export function SidebarSearch({ pages }: SidebarSearchProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Only navigable leaves are searchable. Substring match, case-insensitive,
  // on displayName. Server has already permission-filtered the list, so
  // anything that survives is something the user can navigate to.
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return pages.filter(
      (p) => p.url !== null && p.displayName.toLowerCase().includes(q),
    );
  }, [pages, query]);

  // Close dropdown on click outside.
  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      const target = e.target;
      if (!containerRef.current) return;
      if (target instanceof Node && !containerRef.current.contains(target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const showDropdown = isOpen && query.trim().length > 0;

  function handleSelect(url: string) {
    setQuery('');
    setIsOpen(false);
    navigate(url);
  }

  return (
    <div className="sidebar__search" ref={containerRef}>
      <div className="sidebar__search-input-wrapper">
        <Search
          size={16}
          className="sidebar__search-icon"
          aria-hidden="true"
        />
        <input
          type="search"
          className="sidebar__search-input"
          placeholder={t('sidebar.searchPlaceholder')}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
        />
      </div>
      {showDropdown && (
        <div className="sidebar__search-results">
          {matches.length === 0 ? (
            <div className="sidebar__search-empty">
              {t('sidebar.searchNoResults')}
            </div>
          ) : (
            matches.map((p) => {
              const Icon = resolveIcon(p.iconName);
              return (
                <button
                  key={p.pageId}
                  type="button"
                  className="sidebar__search-result"
                  onClick={() => p.url && handleSelect(p.url)}
                >
                  {Icon && <Icon size={16} />}
                  <span className="sidebar__search-result-name">
                    {p.displayName}
                  </span>
                  {p.url && (
                    <span className="sidebar__search-result-url">{p.url}</span>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
